/**
 * StatusTimeline — the privacy-safe vertical status timeline for a portal report.
 * Consumes the item's `timeline` array (the backend-collapsed public rungs, each
 * with a PAST relative_time). Replaces the old inline emoji breadcrumb.
 *
 * Each entry is a row: a node column (h-7 w-7 circle) + a full-height connector +
 * the label / relative_time. PAST entries render as muted slate nodes; the LAST
 * entry is the CURRENT state — a terminal-success emerald node for verified /
 * you_confirmed, otherwise a white ring-accented "now" node (gentle pulse). The
 * connector lives on the full-height <li> (not the fixed-height node) so it reaches
 * the next node; the last row carries no connector.
 *
 * Renders nothing for an empty timeline (an untriaged report).
 */
import { Check } from 'lucide-react';
import { clientPillLabel } from '@/lib/portal/clientRung';
import { cn } from '@/lib/utils';
import {
  getStatusConfig,
  CURRENT_NODE_RING,
  CURRENT_NODE_TEXT,
} from '@/lib/portal/statusMap';
import type { FeedbackTimelineEntry } from '@/lib/schemas';

interface StatusTimelineProps {
  timeline: FeedbackTimelineEntry[];
  testId?: string;
  entryTestId?: string;
}

export function StatusTimeline({
  timeline,
  testId = 'portal-issue-timeline',
  entryTestId = 'portal-timeline-entry',
}: StatusTimelineProps) {
  if (!timeline || timeline.length === 0) return null;

  return (
    <ol className="relative mt-2" data-testid={testId}>
      {timeline.map((entry, i) => {
        const isLast = i === timeline.length - 1;
        const config = getStatusConfig(entry.key, entry.label);
        const Icon = config.icon;
        const terminalSuccess =
          isLast && (entry.key === 'verified' || entry.key === 'you_confirmed');

        return (
          <li
            key={`${entry.key}-${i}`}
            className={cn('relative flex gap-4', !isLast && 'pb-6')}
            data-testid={entryTestId}
          >
            {/* Connector on the full-height <li> so it reaches the next node. */}
            {!isLast && (
              <span
                aria-hidden
                className="absolute left-3.5 top-7 bottom-0 w-px bg-slate-400"
              />
            )}

            {/* Node */}
            {terminalSuccess ? (
              <span className="relative z-10 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600">
                <Check className="h-4 w-4 text-white" aria-hidden />
              </span>
            ) : isLast ? (
              <span
                className={cn(
                  'relative z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white ring-2 ring-offset-2 animate-pulse',
                  CURRENT_NODE_RING[config.tone],
                )}
              >
                <Icon className={cn('h-4 w-4', CURRENT_NODE_TEXT[config.tone])} aria-hidden />
              </span>
            ) : (
              <span className="relative z-10 flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 ring-1 ring-inset ring-slate-200">
                <Icon className="h-4 w-4 text-slate-500" aria-hidden />
              </span>
            )}

            {/* Content */}
            <div className="pt-1">
              <p
                className={cn(
                  'text-sm',
                  isLast ? 'font-semibold text-slate-900' : 'font-medium text-slate-600',
                )}
              >
                {clientPillLabel(entry.key) ?? entry.label}
              </p>
              {entry.relative_time && (
                <p className="text-xs text-slate-400">{entry.relative_time}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
